/**
 * Executes a callback function after a specified delay and returns functions to cancel the timer or abort the operation.
 * @param callback - Function to execute after the delay that returns an abort function
 * @param delay - Time in milliseconds to wait before executing the callback
 * @returns A tuple containing [cancel, abort] functions
 */
export const runWithTimer = (
  callback: () => VoidFunction,
  delay: number,
): [VoidFunction, VoidFunction] => {
  // Create a reference to store the timer ID
  // Create a reference to store the abort function
  const abortFnRef = {
    current: (): void => {},
  };

  // Start the timer
  const timer = setTimeout((): void => {
    // When the timer fires, execute the callback and store the returned abort function
    abortFnRef.current = callback();
  }, delay);

  // Return a tuple of functions
  return [
    // Cancel function: clears the timer
    (): void => {
      clearTimeout(timer);
    },
    // Abort function: calls the abort function returned by the callback
    (): void => {
      abortFnRef.current();
    },
  ];
};
